<template>
  <div class="reset-wrapper">
    <div class="reset-box">

      <!-- Logo -->
      <div class="reset-logo">
        <img src="../../assets/images/logo/LogoVls.png" alt="VSLBooster" style="height:48px">
      </div>

      <!-- Token inválido -->
      <template v-if="tokenInvalid">
        <div class="reset-icon reset-icon--error">&#128683;</div>
        <h1 class="reset-title">Link inválido ou expirado</h1>
        <p class="reset-subtitle">Este link de redefinição de senha não é mais válido.<br>Solicite um novo link abaixo.</p>
        <router-link :to="{ name: 'auth-reset-password-v1' }" class="reset-btn reset-btn--outline">
          Solicitar novo link
        </router-link>
      </template>

      <!-- Sucesso -->
      <template v-else-if="success">
        <div class="reset-icon reset-icon--success">&#10003;</div>
        <h1 class="reset-title">Senha alterada!</h1>
        <p class="reset-subtitle">Sua senha foi redefinida com sucesso.<br>Você já pode fazer login com a nova senha.</p>
        <router-link :to="{ name: 'auth-login' }" class="reset-btn">
          Ir para o login
        </router-link>
      </template>

      <!-- Formulário -->
      <template v-else>
        <div class="reset-icon">&#128274;</div>
        <h1 class="reset-title">Criar nova senha</h1>
        <p class="reset-subtitle">Escolha uma senha segura para proteger sua conta.</p>

        <div v-if="errorMsg" class="reset-alert">
          {{ errorMsg }}
        </div>

        <form @submit.prevent="submit">
          <!-- Nova senha -->
          <div class="reset-field">
            <label class="reset-label">Nova senha</label>
            <div class="reset-input-wrap" :class="{ 'reset-input-wrap--error': errors.password }">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="Mínimo 6 caracteres"
                class="reset-input"
                autocomplete="new-password"
              >
              <button type="button" class="reset-eye" @click="showPassword = !showPassword" tabindex="-1">
                <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="reset-error">{{ errors.password }}</span>

            <!-- Força da senha -->
            <div v-if="password" class="reset-strength">
              <div class="reset-strength__bars">
                <div v-for="i in 4" :key="i" class="reset-strength__bar" :class="strengthBarClass(i)" />
              </div>
              <span class="reset-strength__label" :class="'reset-strength__label--' + strengthLevel.key">{{ strengthLevel.label }}</span>
            </div>
          </div>

          <!-- Confirmar senha -->
          <div class="reset-field">
            <label class="reset-label">Confirmar nova senha</label>
            <div class="reset-input-wrap" :class="{ 'reset-input-wrap--error': errors.confirm }">
              <input
                :type="showConfirm ? 'text' : 'password'"
                v-model="confirm"
                placeholder="Repita a senha"
                class="reset-input"
                autocomplete="new-password"
              >
              <button type="button" class="reset-eye" @click="showConfirm = !showConfirm" tabindex="-1">
                <svg v-if="!showConfirm" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>
            </div>
            <span v-if="errors.confirm" class="reset-error">{{ errors.confirm }}</span>
            <span v-if="!errors.confirm && confirm && password === confirm" class="reset-match">&#10003; Senhas conferem</span>
          </div>

          <button type="submit" class="reset-btn" :disabled="loading">
            <span v-if="loading" class="reset-spinner" />
            {{ loading ? 'Salvando...' : 'Salvar nova senha' }}
          </button>
        </form>

        <div class="reset-back">
          <router-link :to="{ name: 'auth-login' }">&#8592; Voltar ao login</router-link>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
export default {
  name: 'AlterarSenhaToken',
  data() {
    return {
      token: '',
      password: '',
      confirm: '',
      showPassword: false,
      showConfirm: false,
      loading: false,
      success: false,
      tokenInvalid: false,
      errorMsg: '',
      errors: { password: '', confirm: '' },
    }
  },
  computed: {
    strengthScore() {
      const p = this.password
      if (!p) return 0
      let score = 0
      if (p.length >= 6) score++
      if (p.length >= 10) score++
      if (/[A-Z]/.test(p) && /[0-9]/.test(p)) score++
      if (/[^A-Za-z0-9]/.test(p)) score++
      return score
    },
    strengthLevel() {
      const levels = [
        { key: 'weak', label: 'Fraca' },
        { key: 'fair', label: 'Razoável' },
        { key: 'good', label: 'Boa' },
        { key: 'strong', label: 'Forte' },
      ]
      return levels[Math.max(0, this.strengthScore - 1)] || levels[0]
    },
  },
  created() {
    this.token = this.$route.query.token || ''
    if (!this.token) {
      this.tokenInvalid = true
    }
  },
  methods: {
    strengthBarClass(i) {
      if (i > this.strengthScore) return 'reset-strength__bar--empty'
      const map = ['weak', 'fair', 'good', 'strong']
      return 'reset-strength__bar--' + map[this.strengthScore - 1]
    },
    validate() {
      this.errors = { password: '', confirm: '' }
      let ok = true
      if (!this.password || this.password.length < 6) {
        this.errors.password = 'A senha deve ter pelo menos 6 caracteres.'
        ok = false
      }
      if (this.password !== this.confirm) {
        this.errors.confirm = 'As senhas não conferem.'
        ok = false
      }
      return ok
    },
    async submit() {
      if (!this.validate()) return
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await fetch('/api/dkbackend/resetPassword', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: this.token, password: this.password }),
        })
        const data = await res.json()
        if (!res.ok) {
          if (res.status === 400 && data.error && data.error.includes('inválido')) {
            this.tokenInvalid = true
          } else {
            this.errorMsg = data.error || 'Erro ao redefinir senha. Tente novamente.'
          }
        } else {
          this.success = true
        }
      } catch (e) {
        this.errorMsg = 'Erro de conexão. Verifique sua internet e tente novamente.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.reset-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0D1B2E 0%, #0a2240 50%, #0f172a 100%);
  padding: 24px 16px;
}

.reset-box {
  background: #ffffff;
  border-radius: 20px;
  padding: 48px 44px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.reset-logo {
  margin-bottom: 32px;
}

.reset-icon {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #FFF7ED, #FED7AA);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 20px;
}
.reset-icon--success {
  background: linear-gradient(135deg, #D1FAE5, #A7F3D0);
  color: #059669;
  font-size: 36px;
  font-weight: 900;
}
.reset-icon--error {
  background: linear-gradient(135deg, #FEE2E2, #FECACA);
  color: #DC2626;
}

.reset-title {
  font-size: 24px;
  font-weight: 900;
  color: #0f172a;
  margin: 0 0 10px;
  letter-spacing: -0.5px;
}

.reset-subtitle {
  font-size: 14px;
  color: #64748b;
  line-height: 1.7;
  margin: 0 0 28px;
}

.reset-alert {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  text-align: left;
  margin-bottom: 20px;
}

.reset-field {
  text-align: left;
  margin-bottom: 20px;
}

.reset-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
}

.reset-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.reset-input-wrap:focus-within {
  border-color: #E86A10;
  box-shadow: 0 0 0 3px rgba(232, 106, 16, 0.12);
  background: #fff;
}
.reset-input-wrap--error {
  border-color: #EF4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.reset-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 13px 16px;
  font-size: 15px;
  color: #0f172a;
  outline: none;
  border-radius: 10px;
}
.reset-input::placeholder { color: #94a3b8; }

.reset-eye {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 14px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}
.reset-eye:hover { color: #E86A10; }

.reset-error {
  display: block;
  font-size: 12px;
  color: #EF4444;
  margin-top: 6px;
}
.reset-match {
  display: block;
  font-size: 12px;
  color: #10B981;
  font-weight: 600;
  margin-top: 6px;
}

/* Força */
.reset-strength {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.reset-strength__bars {
  display: flex;
  gap: 4px;
  flex: 1;
}
.reset-strength__bar {
  height: 4px;
  border-radius: 4px;
  flex: 1;
  transition: background 0.3s;
}
.reset-strength__bar--empty { background: #e2e8f0; }
.reset-strength__bar--weak { background: #EF4444; }
.reset-strength__bar--fair { background: #F59E0B; }
.reset-strength__bar--good { background: #3B82F6; }
.reset-strength__bar--strong { background: #10B981; }
.reset-strength__label {
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.reset-strength__label--weak { color: #EF4444; }
.reset-strength__label--fair { color: #F59E0B; }
.reset-strength__label--good { color: #3B82F6; }
.reset-strength__label--strong { color: #10B981; }

/* Botão */
.reset-btn {
  display: block;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #E86A10, #F59E0B);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 900;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  transition: opacity 0.2s, transform 0.15s;
  box-shadow: 0 6px 20px rgba(232, 106, 16, 0.3);
  margin-bottom: 8px;
}
.reset-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.reset-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.reset-btn--outline {
  background: transparent;
  color: #E86A10;
  border: 2px solid #E86A10;
  box-shadow: none;
  display: inline-block;
  width: auto;
  padding: 14px 36px;
}
.reset-btn--outline:hover { background: #FFF7ED; }

.reset-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}
@keyframes spin { to { transform: rotate(360deg); } }

.reset-back {
  margin-top: 20px;
  font-size: 13px;
}
.reset-back a {
  color: #64748b;
  text-decoration: none;
  transition: color 0.2s;
}
.reset-back a:hover { color: #E86A10; }

@media (max-width: 480px) {
  .reset-box { padding: 36px 24px; }
}
</style>
